import { GameAccountService, IApiGameAccount, IGameAccountInfo } from "ng-league";
import { Store } from "@ngrx/store";
import { first, filter } from "rxjs/operators";
import { RootState, AddGameaccountAction, getUser, getContestants, entryAvailable, LoadLeagueRestrictionsAction } from "@play/state";
import { uniq } from "ramda";

export class GameAccountDialog {
    public close: Function;
    public editable: boolean;
    public valid: boolean;
    public gameAccount: IGameAccountInfo;
    public leagueId: number | null;
    public error: string;
    public loading = false;

    public static $inject = ["GameAccountService", "store"];

    constructor(private GameAccountService: GameAccountService, private store: Store<RootState>) {}

    /**
     * Close the dialog.
     */
    public closeDialog(): void {
        this.close();
    }

    /**
     * Update the users selected game account.
     *
     * @param newGameAccount
     */
    public updateGameAccount(newGameAccount: string): void {
        this.error = "";
        this.loading = true;
        this.GameAccountService.set(this.gameAccount.type, newGameAccount)
            .then((newGameAccount: IApiGameAccount) => {
                this.updateLocal(newGameAccount);
            })
            .catch((reason: any) => {
                this.error = this.getErrorTranslation(reason.data.error);
            })
            .then(() => {
                this.loading = false;
            });
    }

    /**
     * Handle success (game account was set remote by a child component).
     *
     * @param newGameAccount
     */
    public handleSuccess(newGameAccount: IApiGameAccount) {
        this.updateLocal(newGameAccount);
    }

    /**
     * Update local state:
     * Monkeypatch gameaccount in store & close dialog.
     *
     * @param newGameAccount
     */
    protected updateLocal(newGameAccount: IApiGameAccount) {
        this.store
            .select(getUser)
            .pipe(first())
            .subscribe(user => {
                this.store.dispatch(new AddGameaccountAction(+user.entry.id, newGameAccount));
                if (this.leagueId) {
                    this.store
                        .select(getContestants(this.leagueId, +user.entry.id))
                        .pipe(
                            filter(entryAvailable),
                            first()
                        )
                        .subscribe(contestants => {
                            const contestantIds = uniq([...contestants.entry.map(({ id }) => +id), +user.entry.id]);
                            contestantIds.forEach(contestantId =>
                                this.store.dispatch(new LoadLeagueRestrictionsAction(this.leagueId, contestantId))
                            );
                        });
                }
            });

        this.close({ value: newGameAccount });
    }

    /**
     * Get an error translation key for the given ESL API error.
     * If none is found, return generic error translation key.
     *
     * @param error
     *
     * @returns string
     */
    protected getErrorTranslation(error: string): string {
        const errors = {
            gameaccount_create_failed_invalid_id: "game-id-dialog:error-invalid-game-id-format",
            invalid_json: "game-id-dialog:error-invalid-game-id-format",
            invalid_gameaccount_value: "game-id-dialog:error-invalid-game-id-format",
            gameaccount_create_failed_gameaccount_check_exists: "game-id-dialog:error-game-id-change-already-requested",
            gameaccount_must_be_unique_for_provider: "game-id-dialog:error-game-id-already-exists",
            gameaccount_already_exists: "game-id-dialog:error-game-id-already-exists",
            invalid_type: "game-id-dialog:error-unknown-game-id",
        };

        const invalidFormatError = `gameaccount_create_failed_invalid_id_${this.gameAccount.type}`;
        errors[invalidFormatError] = "game-id-dialog:error-invalid-game-id-format";

        return errors[error] || "game-id-dialog:error-unknown-error";
    }
}
